import {
  BayLcgVcgTcgAndPairings,
  BaySizesAndCgs,
  IBayPattern,
  IRowPattern,
  createRowsFromConfig,
} from "@tedivo/tedivo-bay-grid-pure";
import {
  IValidationInput,
  IValidationOutput,
  IValidator,
  TValidatorSpecialScopes,
} from "../types";

import { ILidData } from "open-vessel-definition";

const name = "lidDataForBaysValidator";
const okTranslationKey = "validators:lids.lidDataForBaysValidator.ok";
const errorTranslationKey = "validators:lids.lidDataForBaysValidator.error";
const specialScopes: TValidatorSpecialScopes[] = [];

/** Check if lids extend correctly over below bays */
export const lidDataForBaysValidator: IValidator<ILidDataValidatorForBaysOutput> =
  {
    name,
    okTranslationKey,
    errorTranslationKey,
    fn: lidDataForBaysValidatorFn,
    specialScopes,
    severity: "error",
    stage: "semantic",
  };

export function lidDataForBaysValidatorFn({
  data: { json, lcgVcgTcgAndPairings },
}: IValidationInput): IValidationOutput<ILidDataValidatorForBaysOutput> {
  const { sizeSummary, lidData } = json;

  const blpsBelow = lcgVcgTcgAndPairings?.bayLevelPositionsBelow || [];
  const blockBaysAndSizesBy20Bay =
    lcgVcgTcgAndPairings?.blockBaysAndSizesBy20Bay || {};

  if (sizeSummary.maxRow === undefined)
    return {
      name,
      isValid: true,
      messageTranslationKey: okTranslationKey,
      result: {
        validResults: [],
        invalidResults: [],
        allLids: [],
        rowTcgsByBay: {},
      },
    };

  const maxRow = sizeSummary.maxRow;
  const rowTcgsByBay = copyTcgOfRowsByBay(blpsBelow, lcgVcgTcgAndPairings);

  const lidsDataWithValidation = lidData.map((lid) => {
    let notValid: "warning" | "danger" | undefined = undefined;
    let containersWithTcg = 0;

    const bays = [
      ...(blockBaysAndSizesBy20Bay[lid.startIsoBay]?.allBays?.split("-") || []),
      ...(blockBaysAndSizesBy20Bay[lid.endIsoBay]?.allBays?.split("-") || []),
    ].filter((b, idx, arr) => arr.indexOf(b) === idx) as IBayPattern[];

    // Row 00 is only expected if some bay-level below the lid has the centerline row
    // (vessel-wide flag, unless the bay-level opts out)
    const hasCenterLineRow = blpsBelow.some(
      (blp) => bays.includes(blp.isoBay) && blp.hasCenterLineRowForTcgs,
    );

    const rowsArraySorted = createRowsFromConfig(hasCenterLineRow, maxRow);
    const portIndex = rowsArraySorted.indexOf(lid.portIsoRow);
    const stbdIndex = rowsArraySorted.indexOf(lid.starboardIsoRow);

    for (let i = portIndex; i <= stbdIndex; i++) {
      const row = rowsArraySorted[i];
      // ?? (not ||): the TCG of row 00 is 0
      const tcg = bays.reduce((acc, bay) => {
        return acc ?? rowTcgsByBay?.[bay]?.[row];
      }, undefined as number | undefined);

      if (tcg !== undefined) {
        containersWithTcg++;
      }
    }

    if (containersWithTcg === 0) {
      notValid = "danger";
    } else if (containersWithTcg < Math.abs(stbdIndex - portIndex) + 1) {
      notValid = "warning";
    }

    const res: ILidDataWithValidation = { ...lid, notValid };

    return res;
  });

  const invalidResults = lidsDataWithValidation.filter((lid) => lid.notValid);
  const validResults = lidsDataWithValidation.filter((lid) => !lid.notValid);
  const isValid = invalidResults.length === 0;

  return {
    name,
    isValid,
    messageTranslationKey: isValid ? okTranslationKey : errorTranslationKey,
    result: {
      validResults,
      invalidResults,
      allLids: lidsDataWithValidation,
      rowTcgsByBay,
    },
  };
}

export interface ILidDataWithValidation extends ILidData {
  notValid: "warning" | "danger" | undefined;
}

interface IRowsTcgs {
  [row: IRowPattern]: number;
}

interface IBayRowsTcgs {
  [bay: IBayPattern]: IRowsTcgs;
}

//  Helpers
function copyTcgOfRowsByBay(
  blps: BaySizesAndCgs[],
  lcgVcgTcgAndPairings?: BayLcgVcgTcgAndPairings,
) {
  const rowTcgsByBay: IBayRowsTcgs = {};

  blps.forEach(copyTcgOfRows);

  function copyTcgOfRows(blp: BaySizesAndCgs) {
    const perRowAverageBayTcg = blp.perRowAverageBayTcg;

    // Rows with non-restricted slots. perRowAverageBayTcg alone is not enough:
    // rows without a defined TCG (e.g. a vessel without CGs) are not in it.
    const rows = new Set<IRowPattern>(perRowAverageBayTcg.keys());
    blp.cellsInfo.forEach((cell) => {
      if (!cell.restricted) rows.add(cell.row);
    });

    rows.forEach((row) => {
      if (!rowTcgsByBay[blp.isoBay]) rowTcgsByBay[blp.isoBay] = {};
      rowTcgsByBay[blp.isoBay][row] =
        perRowAverageBayTcg.get(row) ||
        lcgVcgTcgAndPairings?.getMissingTcg(row, blp.isoBay, blp.level) ||
        0;
    });
  }

  return rowTcgsByBay;
}

export interface ILidDataValidatorForBaysOutput {
  validResults: ILidDataWithValidation[];
  invalidResults: ILidDataWithValidation[];
  allLids: ILidDataWithValidation[];
  rowTcgsByBay: IBayRowsTcgs;
}
